/**
 * Magic Bytes Validation for Image Files
 *
 * Validates files by comparing their binary signatures (magic bytes) against
 * known image file type patterns. This catches cases where a file's extension
 * doesn't match its actual content (e.g., JPEG data with .png extension).
 */

import { AppLogger } from '@/lib/logger'
import type { Logger } from '@/lib/logger'
import type { ValidationResult } from '@/lib/imageUploadTypes'

// ============================================================================
// Magic Bytes Registry
// ============================================================================

/**
 * Registry of MIME types to their binary file signatures (magic bytes).
 * Used to validate that a file's actual content matches its declared type.
 *
 * Signature format: Array of byte values (0-255 decimal) that appear at file start.
 * For variable-length patterns (e.g., RIFF), only check the fixed prefix.
 */
export const MAGIC_BYTES_REGISTRY: Record<string, number[]> = {
  // JPEG: FF D8 FF (common SOI marker)
  'image/jpeg': [0xff, 0xd8, 0xff],

  // PNG: 89 50 4E 47 (PNG signature)
  'image/png': [0x89, 0x50, 0x4e, 0x47],

  // WebP: 52 49 46 46 ... 57 45 42 50
  // Check for RIFF header followed by WEBP tag at offset 8
  'image/webp': [0x52, 0x49, 0x46, 0x46], // RIFF, will verify WEBP at offset 8

  // TIFF (little-endian): 49 49 2A 00
  'image/tiff': [0x49, 0x49, 0x2a, 0x00],

  // TIFF (big-endian): 4D 4D 00 2A
  'image/tiff+bigendian': [0x4d, 0x4d, 0x00, 0x2a],
}

// ============================================================================
// Logger (Lazy Initialization)
// ============================================================================

let _log: Logger | null = null

function getLog(): Logger {
  if (!_log) {
    _log = AppLogger.getLogger('lib/upload/magicBytes')
  }
  return _log
}

async function readPrefixBytes(file: File, length: number): Promise<Uint8Array> {
  // Prefer reading from a sliced blob, but support environments where
  // `slice(...).arrayBuffer()` is unavailable.
  const sliced = typeof file.slice === 'function' ? file.slice(0, length) : file
  const withArrayBuffer = sliced as unknown as { arrayBuffer?: () => Promise<ArrayBuffer> }

  if (typeof withArrayBuffer.arrayBuffer === 'function') {
    const buffer = await withArrayBuffer.arrayBuffer()
    return new Uint8Array(buffer)
  }

  // Last-resort fallback for test environments that provide FileReader.
  if (typeof FileReader !== 'undefined') {
    const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as ArrayBuffer)
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read file prefix'))
      reader.readAsArrayBuffer(sliced as Blob)
    })
    return new Uint8Array(buffer)
  }

  throw new Error('No supported file-reading API available')
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract bytes from a file's beginning and compare against a signature pattern.
 *
 * @param bytes - Uint8Array of the file's first N bytes
 * @param signature - Expected signature bytes
 * @returns true if signature matches at file start
 */
function matchesSignature(bytes: Uint8Array, signature: number[]): boolean {
  if (bytes.length < signature.length) {
    return false
  }

  for (let i = 0; i < signature.length; i++) {
    if (bytes[i] !== signature[i]) {
      return false
    }
  }

  return true
}

/**
 * Additional validation for WebP files.
 * WebP structure: RIFF header at 0-3, size at 4-7, "WEBP" at 8-11
 *
 * @param bytes - Uint8Array of the file's first N bytes
 * @returns true if WebP signature is valid
 */
function isValidWebP(bytes: Uint8Array): boolean {
  // Check RIFF header already done by parent function
  // Now check for WEBP tag at offset 8
  if (bytes.length < 12) {
    return false
  }

  const webpSignature = [0x57, 0x45, 0x42, 0x50] // "WEBP"
  for (let i = 0; i < 4; i++) {
    if (bytes[8 + i] !== webpSignature[i]) {
      return false
    }
  }

  return true
}

// ============================================================================
// Main Validation Function
// ============================================================================

/**
 * Validate a file's magic bytes against expected signatures.
 *
 * Reads the first 16 bytes of the file (enough for all known image signatures)
 * and compares against the registry. Returns detailed error information on mismatch.
 *
 * @param file - The File object to validate
 * @returns Promise resolving to ValidationResult
 *
 * @example
 * const result = await validateMagicBytes(file)
 * if (!result.valid) {
 *   console.error('Invalid file type:', result.error?.code, result.error?.message)
 * }
 */
export async function validateMagicBytes(file: File): Promise<ValidationResult> {
  const log = getLog()

  try {
    // Read first 16 bytes (enough for all known image signatures)
    const bytes = await readPrefixBytes(file, 16)

    log.debug(`Validating magic bytes for ${file.name} (${file.type})`)

    // Validate only against the file's declared MIME type.
    // TIFF supports both little-endian and big-endian signatures.
    const candidateSignatures: number[][] =
      file.type === 'image/tiff'
        ? [MAGIC_BYTES_REGISTRY['image/tiff'], MAGIC_BYTES_REGISTRY['image/tiff+bigendian']]
        : MAGIC_BYTES_REGISTRY[file.type]
          ? [MAGIC_BYTES_REGISTRY[file.type]]
          : []

    if (candidateSignatures.length === 0) {
      log.warn(`Unsupported MIME type for magic-byte validation: ${file.type}`)
      return {
        valid: false,
        error: {
          code: 'MIME_MISMATCH',
          message: 'File signature does not match declared type. Please verify your file is a valid image.',
        },
      }
    }

    for (const signature of candidateSignatures) {
      if (!matchesSignature(bytes, signature)) continue

      if (file.type === 'image/webp' && !isValidWebP(bytes)) {
        continue
      }

      log.debug(`Magic bytes valid: ${file.name} matches ${file.type}`)
      return { valid: true }
    }

    // No signature matched
    log.warn(
      `Magic bytes mismatch: ${file.name} (declared: ${file.type}, detected: unknown)`
    )

    return {
      valid: false,
      error: {
        code: 'MIME_MISMATCH',
        message: 'File signature does not match declared type. Please verify your file is a valid image.',
      },
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err)
    log.error(`Error validating magic bytes for ${file.name}: ${errorMessage}`)

    return {
      valid: false,
      error: {
        code: 'VALIDATION_ERROR',
        message: 'Could not verify file type. Please try another file.',
      },
    }
  }
}
